import React, { useEffect, useState } from 'react';
import Box from '../Box/Box';
import name from './name';
import InputError from '../InputError';
import TextFieldCharacterCountLabel from './TextFieldCharacterCountLabel';
import InputHelper from '../InputHelper';

type TTextFieldFooter = {
  helperText?: string;
  errorText?: string;
  charsRemainingText?: string;
  charsExceededText?: string;
  maxLength?: number;
  characterCount?: number;
  isFocused?: boolean;
  isRequired?: boolean;
  isLive?: boolean;
};

const getLiveAnnouncement = (
  charsExceeded: boolean,
  exceededAmount: number,
  charsExceededText: string,
  errorText?: string,
  shouldShowCharacterCount?: boolean,
  characterCount?: number,
  maxLength?: number,
  // eslint-disable-next-line max-params
) => {
  if (charsExceeded) return `${charsExceededText} by ${exceededAmount} characters.`;
  if (errorText) return errorText;
  if (shouldShowCharacterCount && maxLength) return `${characterCount} of ${maxLength} characters used.`;
  return '';
};

const hasMaxLength = (maxLength: number) => maxLength > 0;
const isCharsExceeded = (characterCount: number, maxLength: number) => characterCount > maxLength && maxLength !== 0;
const getExceededAmount = (characterCount: number, maxLength: number) => characterCount - maxLength;
const shouldShowCharacterCount = (
  maxLengthExists: boolean,
  charsExceeded: boolean,
  errorText?: string,
  isRequired?: boolean,
  isFocused?: boolean,
) => maxLengthExists && (charsExceeded || (!errorText && (isRequired || isFocused)));

const shouldDisplayHelperText = (helperText?: string, errorText?: string, charsExceeded?: boolean) =>
  helperText && !errorText && !charsExceeded;

const shouldDisplayCharsExceeded = (charsExceeded: boolean) => charsExceeded;
const shouldDisplayErrorText = (errorText?: string) => Boolean(errorText);

const TextFieldFooter = ({
  errorText,
  maxLength = 0,
  characterCount = 0,
  isFocused,
  isRequired,
  charsExceededText = 'Character limit exceeded',
  charsRemainingText,
  helperText,
  isLive = true,
}: TTextFieldFooter) => {
  const maxLengthExists = hasMaxLength(maxLength);
  const charsExceeded = isCharsExceeded(characterCount, maxLength);
  const exceededAmount = getExceededAmount(characterCount, maxLength);
  const shouldDisplayCharacterCount = shouldShowCharacterCount(
    maxLengthExists,
    charsExceeded,
    errorText,
    isRequired,
    isFocused,
  );

  const [liveAnnouncement, setLiveAnnouncement] = useState('');

  useEffect(() => {
    setLiveAnnouncement(
      getLiveAnnouncement(
        charsExceeded,
        exceededAmount,
        charsExceededText,
        errorText,
        shouldDisplayCharacterCount,
        characterCount,
        maxLength,
      ),
    );
  }, [
    charsExceeded,
    exceededAmount,
    errorText,
    characterCount,
    maxLength,
    shouldDisplayCharacterCount,
    charsExceededText,
  ]);

  return (
    <Box parent={name} name="footer" as="div">
      <div>
        {shouldDisplayHelperText(helperText, errorText, charsExceeded) && (
          <InputHelper id="textField-Footer-helper-text">{helperText || ''}</InputHelper>
        )}
        {shouldDisplayCharsExceeded(charsExceeded) && <InputError errorText={charsExceededText} />}
        {shouldDisplayErrorText(errorText) && <InputError errorText={errorText || ''} />}
      </div>

      {shouldDisplayCharacterCount && (
        <TextFieldCharacterCountLabel
          characterCount={characterCount}
          charsRemainingText={charsRemainingText}
          maxLength={maxLength}
          aria-live="polite"
          aria-describedby="live-announcement"
        />
      )}

      <div
        id="live-announcement"
        role="alert"
        aria-live={isLive ? 'polite' : 'off'}
        aria-atomic="true"
        style={{
          position: 'absolute',
          height: 0,
          width: 0,
          overflow: 'hidden',
          display: liveAnnouncement ? 'block' : 'none',
        }}
      >
        {liveAnnouncement}
      </div>
    </Box>
  );
};

export default TextFieldFooter;
